import { useCallback, useMemo } from 'react';

import { DataGridPro, DataGridProProps, GridRow, gridClasses } from '@mui/x-data-grid-pro';

import noop from 'lodash/noop';

import Loading from '@/components/Loading';
import { useSearchParams } from '@/routes';

import EmptyState from './EmptyState';
import { DataGridProps } from './types';

function DataGrid<T = unknown>({
  rows,
  rowTestId,
  pageSize,
  emptyState,
  disableSelection,
  columns,
  getRowId,
  loading,
  getRowHeight,
  onRowClick,
  disableRowHover,
  sx,
  autoHeight,
  disableAutoHeight,
  pagination = true,
  ...props
}: DataGridProps<T>) {
  const [searchParams, setSearchParam] = useSearchParams();

  const currentPage = useMemo(
    () => parseInt(searchParams.get('page') ?? '1', 10) - 1,
    [searchParams],
  );

  const pageItems = useMemo(() => {
    const start = pageSize * currentPage;
    const end = start + pageSize;
    return rows.slice(start, end);
  }, [rows, pageSize, currentPage]);

  const onPaginationModelChange = useCallback<DataGridProProps['onPaginationModelChange']>(
    ({ page }) => setSearchParam('page', (page + 1).toString()),
    [setSearchParam],
  );

  return (
    <DataGridPro
      pagination={pagination}
      hideFooterSelectedRowCount
      disableColumnFilter
      disableColumnResize
      autoHeight={autoHeight || (rows.length > 0 && !disableAutoHeight)}
      sx={{
        background: (theme) => theme.palette.background.default,
        height: rows.length === 0 && 400,
        ...(disableRowHover && {
          [`& .${gridClasses.row}:hover, & .${gridClasses.row}.Mui-hovered`]: {
            background: 'transparent',
            cursor: 'inherit',
          },
        }),
        ...sx,
      }}
      disableRowSelectionOnClick={disableSelection}
      rows={pagination ? pageItems : rows}
      columns={columns}
      getRowHeight={getRowHeight}
      paginationModel={
        pagination
          ? {
              page: currentPage,
              pageSize,
            }
          : undefined
      }
      pageSizeOptions={pagination ? [pageSize] : undefined}
      rowCount={rows.length}
      onPaginationModelChange={pagination ? onPaginationModelChange : noop}
      onRowClick={onRowClick}
      paginationMode="server"
      getRowId={getRowId}
      loading={loading}
      slots={{
        columnResizeIcon: () => null,
        loadingOverlay: Loading,
        row: (props) => <GridRow {...props} {...(rowTestId && { 'data-cy': rowTestId })} />,
        ...(emptyState && { noRowsOverlay: () => emptyState }),
      }}
      {...props}
    />
  );
}

DataGrid.EmptyState = EmptyState;

export default DataGrid;
